import subcategoryService from '../../../services/subcategoryService';
import filterService from '../../../services/filterService';

import {
    setSubCategoryLoading,
    setSubCategoryError,
    setSubCategoryList,
    setSubCategoryCurrent,
    setSubCategoryPagination,
    setSubCategorySort,
    setSubCategoryFilters,
    setSubCategoryDefaultFilter,
    setSubCategorySettings,
    clearSubCategoryCurrent,
    clearSubCategoryError,
    setSubCategoryFilterLoading,
    changeSubCategoryFilterCondition,
    addSubCategoryFilterCondition,
    updateSubCategoryFilterConditionRule,
    updateSubCategoryFilterConditionValue,
    removeSubCategoryFilterCondition,
    resetSubCategoryFiltersAndPagination
} from './subcategorySlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().subcategory.isSettingsInitialized) {
            dispatch(setSubCategoryLoading(true));
            const response = await subcategoryService.getSettings();

            // Set the settings
            dispatch(setSubCategorySettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setSubCategoryDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setSubCategoryError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setSubCategoryLoading(false));
    }
};

export const fetchSubcategories = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().subcategory;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setSubCategoryLoading(true));
        const response = await subcategoryService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setSubCategoryList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchSubcategories:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setSubCategoryError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setSubCategoryLoading(false));
    }
};

export const fetchSubCategory = (id) => async (dispatch) => {
    try {
        dispatch(setSubCategoryLoading(true));
        const response = await subcategoryService.getOne(id);
        dispatch(setSubCategoryCurrent(response.data));
    } catch (error) {
        dispatch(setSubCategoryError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setSubCategoryLoading(false));
    }
};

export const createSubCategory = (data) => async (dispatch) => {
    try {
        const response = await subcategoryService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateSubCategory = (id, data) => async (dispatch) => {
    try {
        const response = await subcategoryService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteSubCategory = (id) => async (dispatch) => {
    try {
        dispatch(setSubCategoryLoading(true));
        const response = await subcategoryService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setSubCategoryError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setSubCategoryLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setSubCategoryPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setSubCategorySort(sort));
};

export const searchSubcategories = (query) => (dispatch, getState) => {
    const currentFilters = getState().subcategory.filters;
    dispatch(setSubCategoryFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setSubCategoryDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeSubCategoryFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addSubCategoryFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateSubCategoryFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateSubCategoryFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeSubCategoryFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setSubCategoryFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().subcategory.settings;
            dispatch(setSubCategorySettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setSubCategoryFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setSubCategoryFilterLoading(false));
        dispatch(setSubCategoryError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetSubCategoryFiltersAndPagination());
};